import { invariant } from '@ardoq/invariant';
import {
  Command,
  CompositeLayoutData,
  EdgesSource,
  GraphBuilder,
  GraphComponent,
  GraphViewerInputMode,
  IFoldingView,
  IGraph,
  IGraphSelection,
  ILayoutAlgorithm,
  IModelItem,
  INode,
  Insets,
  ItemEventArgs,
  LabelsSource,
  LayoutAnchoringStage,
  LayoutData,
  MouseWheelBehaviors,
  NodesSource,
  Point,
  Rect,
  SubgraphLayoutStage,
  SubgraphLayoutStageData,
} from '@ardoq/yfiles';
import { RefObject, useEffect, useRef } from 'react';
import {
  AbsoluteViewport,
  Edge,
  GraphViewProps,
  Viewport,
  IncrementalHierarchicalLayoutAlgorithm,
  Label,
  Layout,
  LayoutAlgorithm,
  Node,
  PartialLayoutAlgorithm,
  Selection,
} from './props';
import { useBackgroundVisuals } from './useBackgroundVisuals';
import { useFocusedItem, useGraphAccessibility } from './useGraphAccessibility';
import { labelFootprint } from './labelMeasurement';
import {
  constructEdgeStyle,
  constructEdgeStyleIndicatorRenderer,
  constructGraphInputMode,
  createFoldingView,
  getGroupExpanderRect,
  constructHierarchicLayout,
  constructHierarchicLayoutData,
  constructIncrementalHierarchicLayoutData,
  constructLabelLayoutParameter,
  constructLabelStyle,
  constructLayoutAlgorithm,
  constructLayoutData,
  constructNodeStyle,
  constructNodeStyleIndicatorRenderer,
  toGraphItemTypes,
  toGroupIndicatorStyle,
  constructPartialLayoutData,
} from './yfilesConstructors';

import {
  EdgeTag,
  NodeTag,
  TaggedGraphNode,
  isEdge,
  isNode,
  isOurEdge,
  nodeId,
} from './tags';
import { useFolding } from './useFolding';

const VIEWPORT_ZOOM_EPSILON = 1e-4;
const VIEWPORT_CENTER_EPSILON = 0.5;

const areViewportsClose = (a: AbsoluteViewport, b: AbsoluteViewport): boolean =>
  Math.abs(a.zoom - b.zoom) < VIEWPORT_ZOOM_EPSILON &&
  Math.abs(a.centerX - b.centerX) < VIEWPORT_CENTER_EPSILON &&
  Math.abs(a.centerY - b.centerY) < VIEWPORT_CENTER_EPSILON;

const readViewport = (graphComponent: GraphComponent): AbsoluteViewport => ({
  type: 'absolute',
  centerX: graphComponent.center.x,
  centerY: graphComponent.center.y,
  zoom: graphComponent.zoom,
});

export type GraphBuilderSources = {
  builder: GraphBuilder;
  nodesSource: NodesSource<Node>;
  edgesSource: EdgesSource<Edge>;
};

const configureLabelCreator = (labelsSource: LabelsSource<Label>) => {
  const labelCreator = labelsSource.labelCreator;
  labelCreator.textProvider = (label: Label) => label.text;
  labelCreator.styleProvider = (label: Label) =>
    constructLabelStyle(label.style);
  labelCreator.layoutParameterProvider = (label: Label) =>
    constructLabelLayoutParameter(label.layout);

  // `updateGraph()` only updates the graph structure; a label's text and style
  // must be re-applied explicitly on the `label-updated` event.
  labelCreator.addEventListener('label-updated', evt => {
    labelCreator.updateText(evt.graph, evt.item, evt.dataItem);
    labelCreator.updateStyle(evt.graph, evt.item, evt.dataItem);
  });
};

const createGraphBuilderSources = (graph: IGraph): GraphBuilderSources => {
  const builder = new GraphBuilder(graph);

  const nodesSource = builder.createNodesSource<Node>([], node => node.id);
  nodesSource.parentIdProvider = node => node.parentId ?? null;
  const nodeCreator = nodesSource.nodeCreator;
  nodeCreator.styleProvider = node => constructNodeStyle(node.style);
  nodeCreator.tagProvider = node => ({ node }) satisfies NodeTag;
  nodeCreator.layoutProvider = nodeLayout;
  const nodeLabelsSource = nodeCreator.createLabelsSource(node => node.labels);
  configureLabelCreator(nodeLabelsSource);

  // `updateGraph()` only updates the graph structure; style, tag and labels of
  // existing nodes must be re-applied explicitly on the `node-updated` event.
  nodeCreator.addEventListener('node-updated', evt => {
    nodeCreator.updateStyle(evt.graph, evt.item, evt.dataItem);
    nodeCreator.updateTag(evt.graph, evt.item, evt.dataItem);
    nodeCreator.updateLabels(evt.graph, evt.item, evt.dataItem);
  });

  const edgesSource = builder.createEdgesSource<Edge>(
    [],
    edge => edge.source,
    edge => edge.target,
    edge => edge.id
  );
  const edgeCreator = edgesSource.edgeCreator;
  edgeCreator.styleProvider = edge => constructEdgeStyle(edge.style);
  edgeCreator.tagProvider = edge => ({ edge }) satisfies EdgeTag;
  const edgeLabelsSource = edgeCreator.createLabelsSource(edge => edge.labels);
  configureLabelCreator(edgeLabelsSource);

  edgeCreator.addEventListener('edge-updated', evt => {
    edgeCreator.updateStyle(evt.graph, evt.item, evt.dataItem);
    edgeCreator.updateTag(evt.graph, evt.item, evt.dataItem);
    edgeCreator.updateLabels(evt.graph, evt.item, evt.dataItem);
  });

  builder.buildGraph();

  return { builder, nodesSource, edgesSource };
};

const populateGraph = (
  sources: GraphBuilderSources,
  nodes: Node[],
  edges: Edge[]
) => {
  sources.builder.setData(sources.nodesSource, nodes);
  sources.builder.setData(sources.edgesSource, edges);
  sources.builder.updateGraph();
};

const topologyKey = (nodes: Node[], edges: Edge[]): string =>
  JSON.stringify([
    nodes.map(node => [node.id, node.parentId ?? null]).sort(),
    edges.map(edge => [edge.id, edge.source, edge.target]).sort(),
  ]);

const zoomInOnNodes = (
  graphComponent: GraphComponent,
  nodes: TaggedGraphNode[],
  layoutProps: Layout
) => {
  const bounds = nodes.reduce(
    (rect, node) => Rect.add(rect, node.layout.toRect()),
    nodes[0].layout.toRect()
  );
  const padded = bounds.getEnlarged(
    new Insets(layoutProps.fitGraphBoundsInsets)
  );
  graphComponent.zoomTo(padded);
  if (layoutProps.limitFitContentZoom && graphComponent.zoom > 1) {
    graphComponent.zoom = 1;
  }
};

const fitViewport = (
  graphComponent: GraphComponent,
  layoutProps: Layout,
  builder: GraphBuilder
) => {
  graphComponent.limitFitContentZoom = layoutProps.limitFitContentZoom;

  if (layoutProps.zoomToNodes?.length) {
    const targetNodes = layoutProps.zoomToNodes
      .map(id => builder.getNodeById<string>(id))
      .filter(isNode);

    if (targetNodes.length > 0) {
      return zoomInOnNodes(graphComponent, targetNodes, layoutProps);
    }
  }

  graphComponent.fitGraphBounds(layoutProps.fitGraphBoundsInsets);
};

const layoutIncremental = (
  graphComponent: GraphComponent,
  algorithm: IncrementalHierarchicalLayoutAlgorithm
) => {
  const graph = graphComponent.graph;

  const hierarchicalAlgorithm = constructHierarchicLayout(algorithm.options);
  const layoutData = new CompositeLayoutData(
    constructIncrementalHierarchicLayoutData(algorithm.data)
  );

  // Layout the pre-layout nodes first
  graph.applyLayout(
    new SubgraphLayoutStage({
      coreLayout: hierarchicalAlgorithm,
      enabled: true,
    }),
    layoutData
  );

  // Layout the rest of the graph incrementally
  // Use a LayoutAnchoringStage to keep the pre-layout nodes in the same place
  // in the view port
  hierarchicalAlgorithm.fromSketchMode = true;
  const layoutAlgorithm = new LayoutAnchoringStage(hierarchicalAlgorithm);
  graph.applyLayout(layoutAlgorithm, layoutData);

  return {
    layoutAlgorithm,
    layoutData,
  };
};

const NODE_PADDING = 8;

const labelSize = (label: Label): { width: number; height: number } => {
  const { font, insets, icon } = label.style;
  const padding = Insets.from(insets);
  const { width, height } = labelFootprint({
    font,
    text: label.text,
    hasIcon: Boolean(icon),
  });
  return {
    width: width + padding.horizontalInsets,
    height: height + padding.verticalInsets,
  };
};

/**
 * Size a leaf node at creation (wired via `NodeCreator.layoutProvider`): canvas
 * nodes take the fixed size declared on their style, shape nodes are fit to their
 * label(s). Position is left at the origin — the layout algorithm places the node,
 * and resizes group nodes to fit their children. Returns null for other /
 * unlabeled nodes so they keep their style's intrinsic size.
 */
const nodeLayout = (node: Node): Rect | null => {
  if (node.style.type === 'canvas') {
    return new Rect(0, 0, node.style.size.width, node.style.size.height);
  }
  if (node.style.type !== 'shape' || node.labels.length === 0) return null;

  let maxWidth = 0;
  let maxHeight = 0;
  for (const label of node.labels) {
    const { width, height } = labelSize(label);
    maxWidth = Math.max(maxWidth, width);
    maxHeight = Math.max(maxHeight, height);
  }
  return new Rect(
    0,
    0,
    maxWidth + NODE_PADDING * 2,
    maxHeight + NODE_PADDING * 2
  );
};

const layoutPartial = (
  graphComponent: GraphComponent,
  algorithm: PartialLayoutAlgorithm
) => {
  const graph = graphComponent.graph;
  const partialNodeIds = new Set(algorithm.data.partialNodes);

  // Layout fixed nodes with hierarchical layout + group padding + node halo
  const coreLayout = constructHierarchicLayout(algorithm.options.coreLayout);
  graph.applyLayout(
    new SubgraphLayoutStage({ coreLayout, enabled: true }),
    new CompositeLayoutData(
      new SubgraphLayoutStageData({
        subgraphNodes: (node: INode) => !partialNodeIds.has(node.tag?.node?.id),
      }),
      constructHierarchicLayoutData(algorithm.data, graph)
    )
  );

  // Place partial nodes around the fixed ones
  const layoutAlgorithm = constructLayoutAlgorithm(algorithm, graph);
  const layoutData = constructPartialLayoutData(algorithm.data);
  graph.applyLayout(layoutAlgorithm, layoutData);

  return { layoutAlgorithm, layoutData };
};

const layoutGraph = (graphComponent: GraphComponent, layoutProps: Layout) => {
  const algorithm = layoutProps.algorithm;
  if (algorithm.type === 'incremental-hierarchical') {
    return layoutIncremental(graphComponent, algorithm);
  }
  if (algorithm.type === 'partial') {
    return layoutPartial(graphComponent, algorithm);
  }
  const graph = graphComponent.graph;

  const layoutAlgorithm = constructLayoutAlgorithm(
    layoutProps.algorithm,
    graph
  );
  const layoutData = new CompositeLayoutData(
    constructLayoutData(layoutProps.algorithm, graph)
  );
  graph.applyLayout(layoutAlgorithm, layoutData);

  return {
    layoutAlgorithm,
    layoutData,
  };
};

const toMorphLayoutAlgorithm = (
  graph: IGraph,
  algorithm: LayoutAlgorithm,
  storedAlgorithm: ILayoutAlgorithm
): ILayoutAlgorithm => {
  if (algorithm.type !== 'partial') return storedAlgorithm;
  return constructLayoutAlgorithm(algorithm, graph);
};

const isGroupLike = (graph: IGraph, node: INode): boolean => {
  const foldingView = graph.foldingView;
  if (!foldingView) return graph.isGroupNode(node);
  const masterNode = foldingView.getMasterItem(node);
  return (
    masterNode !== null &&
    foldingView.manager.masterGraph.isGroupNode(masterNode)
  );
};

const cleanupGraphComponent = (component: GraphComponent) => {
  component.cleanUp();
};

const verifyGraph = (nodes: Node[], edges: Edge[]) => {
  const nodeIds = new Set(nodes.map(node => node.id));

  for (const node of nodes) {
    if (node.parentId) {
      invariant(
        nodeIds.has(node.parentId),
        `Parent node ${node.parentId} not found for node ${node.id}`
      );
    }
  }

  for (const edge of edges) {
    invariant(
      nodeIds.has(edge.source),
      `Source node ${edge.source} not found for reference ${edge.id}`
    );
    invariant(
      nodeIds.has(edge.target),
      `Target node ${edge.target} not found for reference ${edge.id}`
    );
  }
};

/** Prevent recursion when making changes from event handlers */
let controlledEvent = false;
const isControlledEvent = () => controlledEvent;
const withControlledEvents = (fn: () => void) => {
  controlledEvent = true;
  try {
    fn();
  } finally {
    controlledEvent = false;
  }
};

const applySelectionChange = (
  selection: Selection,
  item: IModelItem,
  itemSelected: boolean
): Selection => {
  if (isNode(item)) {
    const nodeId = item.tag.node.id;
    const otherNodes = selection.nodes.filter(id => id !== nodeId);
    return {
      ...selection,
      nodes: itemSelected ? [...otherNodes, nodeId] : otherNodes,
    };
  }
  if (isEdge(item)) {
    const edgeId = item.tag.edge.id;
    const otherEdges = selection.edges.filter(id => id !== edgeId);
    return {
      ...selection,
      edges: itemSelected ? [...otherEdges, edgeId] : otherEdges,
    };
  }

  return selection;
};

const createSelectionChangedListener = (
  controlledSelection: Selection | undefined,
  onSelectionChange: ((selection: Selection) => void) | undefined,
  itemSelected: boolean
) => {
  let hasScheduled = false;
  let uncontrolledSelection: Selection = {
    nodes: [],
    edges: [],
    ...controlledSelection,
  };

  const triggerOnSelectionChange = () => {
    hasScheduled = false;
    onSelectionChange?.(uncontrolledSelection);
  };

  const yfilesSelectionEventHandler = (
    event: ItemEventArgs<IModelItem>,
    graphSelection: IGraphSelection
  ) => {
    if (isControlledEvent()) return;
    if (!hasScheduled && onSelectionChange) {
      hasScheduled = true;
      window.setTimeout(triggerOnSelectionChange, 0);
    }

    if (controlledSelection) {
      withControlledEvents(() => {
        if (itemSelected) {
          graphSelection.remove(event.item);
        } else {
          graphSelection.add(event.item);
        }
      });
    }

    uncontrolledSelection = applySelectionChange(
      uncontrolledSelection,
      event.item,
      itemSelected
    );
  };
  return yfilesSelectionEventHandler;
};

const reconcileSelection = (
  graphComponent: GraphComponent,
  selection: Selection
) => {
  const selectedNodes = new Set(selection.nodes);
  const selectedEdges = new Set(selection.edges);

  withControlledEvents(() => {
    for (const node of graphComponent.graph.nodes) {
      const id = nodeId(node);
      if (id === undefined) continue; // skip yFiles-managed folder nodes
      const isSelected = selectedNodes.has(id);
      if (isSelected) {
        graphComponent.selection.add(node);
      } else {
        graphComponent.selection.remove(node);
      }
    }

    for (const edge of graphComponent.graph.edges) {
      if (!isOurEdge(edge)) continue; // skip aggregated folding edges
      const isSelected = selectedEdges.has(edge.tag.edge.id);
      if (isSelected) {
        graphComponent.selection.add(edge);
      } else {
        graphComponent.selection.remove(edge);
      }
    }
  });
};

export const useYFiles = ({
  nodes,
  edges,
  layout,
  component,
  selection,
  disableMouseScroll,
  accessibility,
  onEdgeClick,
  onNodeClick,
  onNodeContextMenu,
  onEdgeContextMenu,
  onSelectionChange,
  onCanvasClick,
  backgroundGroups,
  invalidateToken,
  viewport,
  onViewportChange,
  collapsibleGroups,
  collapsedGroupIds,
  relayoutToken,
  zoomInToken,
  zoomOutToken,
  collapseAllToken,
  expandAllToken,
  onFoldStateChange,
}: GraphViewProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const graphComponentRef = useRef<GraphComponent | null>(null);
  const foldingViewRef = useRef<IFoldingView | null>(null);
  const inputModeRef = useRef<GraphViewerInputMode | null>(null);
  const layoutRef = useRef<{
    algorithm: ILayoutAlgorithm;
    data: LayoutData;
  } | null>(null);
  const hasFreshLayout = useRef(true);
  const previousTopologyKeyRef = useRef<string | null>(null);
  const graphBuilderSourcesRef = useRef<GraphBuilderSources | null>(null);
  // `collapsibleGroups` chooses which graph instance backs the component (plain vs.
  // folding view) at mount and is static for its lifetime — toggling requires a remount.
  // Captured in a ref so the mount effect reads it without depending on it reactively.
  const collapsibleGroupsRef = useRef(collapsibleGroups);
  // Read the latest viewport via a ref so the data-change relayout can decide
  // whether to re-fit, without a viewport change itself re-running that effect
  // (which would relayout the graph on every pan/zoom).
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const graphComponent = new GraphComponent(container);
    graphComponent.minimumZoom = 0.1;
    graphComponent.maximumZoom = 4;
    // Opt-in native folding: back the component with a FoldingManager view graph so group
    // nodes can collapse/expand. The GraphBuilder must build the *master* graph (yFiles
    // throws "make sure to use the master graph in the GraphBuilder" otherwise) — folding
    // mirrors master → view, so the component displays the view graph and all
    // layout/selection code operates on `graphComponent.graph` (the view) unchanged.
    const foldingView = collapsibleGroupsRef.current
      ? createFoldingView()
      : null;
    if (foldingView) {
      graphComponent.graph = foldingView.graph;
      foldingViewRef.current = foldingView;
    }
    graphComponentRef.current = graphComponent;
    graphBuilderSourcesRef.current = createGraphBuilderSources(
      foldingView ? foldingView.manager.masterGraph : graphComponent.graph
    );

    return () => {
      cleanupGraphComponent(graphComponent);
      graphComponentRef.current = null;
      foldingViewRef.current = null;
      graphBuilderSourcesRef.current = null;
    };
  }, []);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent) return;
    if (disableMouseScroll) {
      graphComponent.mouseWheelBehavior = MouseWheelBehaviors.NONE;
    }
  }, [disableMouseScroll]);

  const navigableItems = accessibility
    ? toGraphItemTypes(accessibility.navigableItems)
    : null;

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent) return;

    const inputMode = constructGraphInputMode({
      inputMode: component.inputMode,
      collapsibleGroups: Boolean(collapsibleGroups),
      navigableItems,
    });
    graphComponent.inputMode = inputMode;
    inputModeRef.current = inputMode;
  }, [component.inputMode, collapsibleGroups, navigableItems]);

  useGraphAccessibility({
    graphComponentRef,
    inputModeRef,
    inputMode: component.inputMode,
    accessibility,
  });

  useEffect(() => {
    const inputMode = inputModeRef.current;
    if (!inputMode || !onCanvasClick) return;

    const listener = () => onCanvasClick();
    inputMode.addEventListener('canvas-clicked', listener);
    return () => inputMode.removeEventListener('canvas-clicked', listener);
  }, [onCanvasClick, component.inputMode]);

  useEffect(() => {
    const inputMode = inputModeRef.current;
    if (!inputMode) return;
    const foldingView = foldingViewRef.current;
    const listener = (args: {
      item: unknown;
      location: { x: number; y: number };
      handled: boolean;
    }) => {
      const item = args.item;
      // Clicking a collapsible group's expander toggles folding instead of
      // selecting/inspecting. graph-view owns the expander hit-region (the consumer
      // paints the chevron there via the same getGroupExpanderRect helper).
      if (
        foldingView &&
        isNode(item) &&
        item.tag.node.style.type === 'canvas-group'
      ) {
        const rect = getGroupExpanderRect(item.layout);
        const { x, y } = args.location;
        const hitsExpander =
          x >= rect.x &&
          x <= rect.x + rect.width &&
          y >= rect.y &&
          y <= rect.y + rect.height;
        if (hitsExpander) {
          if (foldingView.isExpanded(item)) {
            foldingView.collapse(item);
          } else {
            foldingView.expand(item);
          }
          args.handled = true;
          return;
        }
      }
      if (isNode(item)) {
        item.tag.node.onClick?.();
        onNodeClick?.(item.tag.node);
      } else if (isEdge(item)) {
        item.tag.edge.onClick?.();
        onEdgeClick?.(item.tag.edge);
      }
    };
    inputMode.addEventListener('item-clicked', listener);
    return () => inputMode.removeEventListener('item-clicked', listener);
  }, [onNodeClick, onEdgeClick, component.inputMode]);

  useEffect(() => {
    const inputMode = inputModeRef.current;
    if (!inputMode || (!onNodeContextMenu && !onEdgeContextMenu)) return;

    const listener = (args: {
      item: unknown;
      location: { x: number; y: number };
      handled: boolean;
    }) => {
      const graphComponent = graphComponentRef.current;
      if (!graphComponent) return;
      const { item, location } = args;
      const position = graphComponent.viewToPageCoordinates(
        graphComponent.worldToViewCoordinates([location.x, location.y])
      );
      if (isNode(item) && onNodeContextMenu) {
        onNodeContextMenu(item.tag.node, { x: position.x, y: position.y });
        args.handled = true;
      } else if (isEdge(item) && onEdgeContextMenu) {
        onEdgeContextMenu(item.tag.edge, { x: position.x, y: position.y });
        args.handled = true;
      }
    };
    inputMode.addEventListener('item-right-clicked', listener);
    return () => inputMode.removeEventListener('item-right-clicked', listener);
  }, [onNodeContextMenu, onEdgeContextMenu, component.inputMode]);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent) return;

    const graph = graphComponent.graph;
    const nodeRenderer = constructNodeStyleIndicatorRenderer(
      component.focusIndicator
    );
    const groupRenderer = constructNodeStyleIndicatorRenderer(
      toGroupIndicatorStyle(component.focusIndicator)
    );
    graph.decorator.nodes.focusRenderer.addFactory((node: INode) =>
      isGroupLike(graph, node) ? groupRenderer : nodeRenderer
    );
    graph.decorator.edges.focusRenderer.addConstant(
      () => true,
      constructEdgeStyleIndicatorRenderer(component.edgeFocusIndicator)
    );
  }, [component.focusIndicator, component.edgeFocusIndicator]);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent) return;

    const graph = graphComponent.graph;
    const nodeRenderer = constructNodeStyleIndicatorRenderer(
      component.selectionIndicator.nodeStyle
    );
    const groupRenderer = constructNodeStyleIndicatorRenderer(
      toGroupIndicatorStyle(component.selectionIndicator.nodeStyle)
    );
    graph.decorator.nodes.selectionRenderer.addFactory(
      (node: TaggedGraphNode) => {
        if (node.tag.node.selectionStyle) {
          return constructNodeStyleIndicatorRenderer(
            node.tag.node.selectionStyle
          );
        }
        return isGroupLike(graph, node) ? groupRenderer : nodeRenderer;
      }
    );
    graphComponent.graph.decorator.edges.selectionRenderer.addConstant(
      () => true,
      constructEdgeStyleIndicatorRenderer(
        component.selectionIndicator.edgeStyle
      )
    );
  }, [component.selectionIndicator]);

  useEffect(() => {
    const sources = graphBuilderSourcesRef.current;
    if (!sources) return;

    verifyGraph(nodes, edges);
    populateGraph(sources, nodes, edges);
  }, [nodes, edges]);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    const sources = graphBuilderSourcesRef.current;
    if (!graphComponent || !sources) return;

    const { layoutAlgorithm, layoutData } = layoutGraph(graphComponent, layout);
    graphComponent.updateContentBounds();
    fitViewport(graphComponent, layout, sources.builder);
    layoutRef.current = { algorithm: layoutAlgorithm, data: layoutData };
    hasFreshLayout.current = true;
  }, [layout]);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    const graphLayout = layoutRef.current;
    if (!graphComponent || !graphLayout) return;

    const topologyKeyValue = topologyKey(nodes, edges);
    const topologyChanged = topologyKeyValue !== previousTopologyKeyRef.current;
    previousTopologyKeyRef.current = topologyKeyValue;

    // Don't morph if the layout is fresh
    if (hasFreshLayout.current) {
      hasFreshLayout.current = false;
      return;
    }

    if (!topologyChanged) return;

    // yFiles does not recompute `contentBounds` after an animated layout, and
    // the viewportLimiter clamps panning to it, so a graph that grows keeps the
    // smaller rect and becomes partly unreachable.
    graphComponent
      .applyLayoutAnimated({
        layout: toMorphLayoutAlgorithm(
          graphComponent.graph,
          layout.algorithm,
          graphLayout.algorithm
        ),
        layoutData: graphLayout.data,
        animationDuration: layout.morphDuration,
        targetBoundsPadding: layout.fitGraphBoundsInsets,
        animateViewport: viewportRef.current?.type !== 'absolute',
      })
      .then(() => {
        if (graphComponentRef.current !== graphComponent) return;
        graphComponent.updateContentBounds();
      });
  }, [
    nodes,
    edges,
    layout.algorithm,
    layout.fitGraphBoundsInsets,
    layout.morphDuration,
  ]);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent || !selection) return;

    reconcileSelection(graphComponent, selection);
  }, [selection]);

  // Folding-specific effects (relayout on collapse/expand, initial collapse, optimize
  // layout). No-op unless the consumer opted into folding.
  useFolding({
    graphComponentRef,
    foldingViewRef,
    layoutRef,
    layout,
    selection,
    collapsedGroupIds,
    relayoutToken,
    collapseAllToken,
    expandAllToken,
    onFoldStateChange,
    reconcileSelection,
  });

  useZoomCommandToken(graphComponentRef, zoomInToken, Command.INCREASE_ZOOM);
  useZoomCommandToken(graphComponentRef, zoomOutToken, Command.DECREASE_ZOOM);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent) return;
    if (!selection && !onSelectionChange) return;

    const addedListener = createSelectionChangedListener(
      selection,
      onSelectionChange,
      true
    );
    const removedListener = createSelectionChangedListener(
      selection,
      onSelectionChange,
      false
    );
    graphComponent.selection.addEventListener('item-added', addedListener);
    graphComponent.selection.addEventListener('item-removed', removedListener);

    return () => {
      graphComponent.selection.removeEventListener('item-added', addedListener);
      graphComponent.selection.removeEventListener(
        'item-removed',
        removedListener
      );
    };
  }, [selection, onSelectionChange]);

  // Opt-in repaint without relayout — e.g. when a canvas node's async icon/image
  // sprite has loaded. Kept separate from the layout effects so a token bump never
  // re-runs layout.
  useEffect(() => {
    if (invalidateToken === undefined || invalidateToken === null) return;
    graphComponentRef.current?.invalidate();
  }, [invalidateToken]);
  useControlledViewport(graphComponentRef, layout, viewport, onViewportChange);

  useBackgroundVisuals({
    graphComponentRef,
    graphBuilderSourcesRef,
    nodes,
    backgroundGroups,
  });

  useFocusedItem({
    graphComponentRef,
    focusedId: accessibility?.focusedId,
    nodes,
    edges,
  });

  return { containerRef };
};

/** One zoom step per click, matching the other yFiles views (Block Diagram). */
const ZOOM_FACTOR = 1.2;

/**
 * Zooms in/out one step when `token` changes, via the native yFiles command (animated,
 * and clamped to the component's min/max zoom). A last-token guard skips the first render
 * so mounting never zooms.
 */
const useZoomCommandToken = (
  graphComponentRef: RefObject<GraphComponent | null>,
  token: unknown,
  command: Command
) => {
  const lastToken = useRef(token);
  useEffect(() => {
    if (token === lastToken.current) return;
    lastToken.current = token;
    graphComponentRef.current?.executeCommand(command, ZOOM_FACTOR);
  }, [graphComponentRef, token, command]);
};

const useControlledViewport = (
  graphComponentRef: RefObject<GraphComponent | null>,
  layout: Layout,
  viewport: Viewport | undefined,
  onViewportChange: ((viewport: AbsoluteViewport) => void) | undefined
) => {
  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handler = () => {
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        const currentViewport = readViewport(graphComponent);
        if (
          viewport?.type === 'absolute' &&
          areViewportsClose(viewport, currentViewport)
        ) {
          return;
        }
        onViewportChange?.(currentViewport);
      }, 50);
    };

    graphComponent.addEventListener('viewport-changed', handler);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      graphComponent.removeEventListener('viewport-changed', handler);
    };
  }, [graphComponentRef, onViewportChange, viewport]);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent || viewport === undefined) return;

    if (viewport.type === 'fit-all-content') {
      handleFitAllContent(graphComponent, layout, onViewportChange);
    } else if (viewport.type === 'absolute') {
      handleAbsoluteViewport(graphComponent, viewport);
    } else if (viewport.type === 'fit-nodes') {
      handleFitNodes(
        graphComponent,
        layout,
        viewport.nodeIds,
        onViewportChange
      );
    }
  }, [graphComponentRef, viewport, onViewportChange, layout]);
};

const handleFitNodes = (
  graphComponent: GraphComponent,
  layout: Layout,
  nodeIds: string[],
  onViewportChange: ((viewport: AbsoluteViewport) => void) | undefined
) => {
  const ids = new Set(nodeIds);
  const targetNodes = [...graphComponent.graph.nodes].filter(
    (node): node is TaggedGraphNode => isNode(node) && ids.has(node.tag.node.id)
  );
  if (targetNodes.length === 0) return;

  // yFiles has no animated fit-to-rect that respects `limitFitContentZoom`, so
  // we let the instant `zoomInOnNodes` resolve the exact target, then replay it
  // as an animation: snap back to the start and `zoomToAnimated` into the target.
  const previousViewport = readViewport(graphComponent);
  zoomInOnNodes(graphComponent, targetNodes, layout);
  const targetViewport = readViewport(graphComponent);

  if (areViewportsClose(previousViewport, targetViewport)) {
    // No change, so no 'viewport-changed' event.
    // Trigger callback manually, so parent can get the absolute coordinates.
    onViewportChange?.(targetViewport);
    return;
  }

  graphComponent.zoomTo(
    previousViewport.zoom,
    new Point(previousViewport.centerX, previousViewport.centerY)
  );
  graphComponent.zoomToAnimated(
    targetViewport.zoom,
    new Point(targetViewport.centerX, targetViewport.centerY)
  );
};

const handleFitAllContent = async (
  graphComponent: GraphComponent,
  layout: Layout,
  onViewportChange: ((viewport: AbsoluteViewport) => void) | undefined
) => {
  graphComponent.limitFitContentZoom = layout.limitFitContentZoom;

  const previousViewport = readViewport(graphComponent);
  await graphComponent.fitGraphBounds(layout.fitGraphBoundsInsets, true);
  const newViewport = readViewport(graphComponent);

  if (areViewportsClose(previousViewport, newViewport)) {
    // No change, so no 'viewport-changed` event.
    // Trigger callback manually, so parent can get the absolute coordinates
    onViewportChange?.(newViewport);
  }
};

const handleAbsoluteViewport = (
  graphComponent: GraphComponent,
  viewport: AbsoluteViewport
) => {
  if (areViewportsClose(readViewport(graphComponent), viewport)) {
    return;
  }

  graphComponent.zoomToAnimated(
    viewport.zoom,
    new Point(viewport.centerX, viewport.centerY)
  );
};
